((root, factory) => {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.SBEventCart = api;
})(typeof window === 'undefined' ? globalThis : window, () => {
  const P = { event: '_sb_event_id', group: '_sb_ticket_group_id', id: '_sb_attendee_id', first: '_sb_attendee_first_name', last: '_sb_attendee_last_name' };
  const publicKeys = ['First name', 'Last name', 'Vorname', 'Nachname', 'Имя', 'Фамилия'];
  const snapshotKeys = ['_sb_event_snapshot_version', '_sb_event_title', '_sb_event_date', '_sb_event_location', '_sb_event_image_url', '_sb_event_locale'];
  const uuid = () => globalThis.crypto.randomUUID();
  const clean = value => String(value ?? '').trim();
  const stable = value => JSON.stringify(value, Object.keys(value).sort());
  function snapshot(config) {
    const data = config.emailSnapshot;
    if (data?.version !== 1) return null;
    const value = key => typeof data[key] === 'string' ? clean(data[key]) : '';
    let image = '';
    try {
      const url = new URL(value('image'));
      if (url.protocol === 'https:' && (url.href.startsWith('https://cdn.shopify.com/s/files/1/0640/9689/5127/') || url.href.startsWith('https://skillsbeauty.com/cdn/shop/'))) image = url.href;
    } catch {}
    return { _sb_event_snapshot_version: '1', _sb_event_title: value('title'), _sb_event_date: value('date'),
      _sb_event_location: value('location'), _sb_event_image_url: image, _sb_event_locale: value('locale') };
  }
  class FlowError extends Error {
    constructor(code) { super(code); this.code = code; }
  }
  const ticket = (item, config) => String(item.product_id) === String(config.productId) || item.properties?.[P.event] === config.eventId;
  const members = (cart, state) => cart.items.filter(item => item.properties?.[P.group] === state.groupId && item.properties?.[P.event] === state.eventId);
  const legacy = (cart, state) => cart.items.filter(item => state.legacyKeys?.includes(item.key) && !item.properties?.[P.group]);
  const owned = (cart, state) => [...members(cart, state), ...legacy(cart, state)];
  const fingerprint = (cart, state) => JSON.stringify(owned(cart, state).map(item => [String(item.variant_id), item.quantity, stable(item.properties || {})]).sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b))));
  const fromCart = (cart, groupId, eventId) => {
    const rows = members(cart, { groupId, eventId });
    return rows.flatMap(item => Array.from({ length: item.quantity }, (_, index) => ({
      id: index ? uuid() : item.properties[P.id] || uuid(),
      first: item.properties[P.first] || '', last: item.properties[P.last] || '',
      properties: item.properties || {},
    })));
  };
  function properties(person, state, labels) {
    const result = { ...(person.properties || {}) };
    publicKeys.forEach(key => delete result[key]);
    if (state.eventSnapshot) {
      snapshotKeys.forEach(key => delete result[key]);
      Object.assign(result, state.eventSnapshot);
    }
    return { ...result, [P.event]: state.eventId, [P.group]: state.groupId, [P.id]: person.id,
      [P.first]: clean(person.first), [P.last]: clean(person.last),
      [labels.first]: clean(person.first), [labels.last]: clean(person.last) };
  }
  function validate(state) {
    if (!state.attendees.length) throw new FlowError('quantity');
    if (new Set(state.attendees.map(person => person.id)).size !== state.attendees.length) throw new FlowError('conflict');
    if (state.attendees.some(person => !clean(person.first) || !clean(person.last))) throw new FlowError('required');
  }
  function allowance(cart, state, config) {
    const variant = config.variants.find(v => String(v.id) === String(state.variantId));
    if (!variant?.available || !config.purchasable) return 0;
    const ours = new Set(owned(cart, state));
    const otherEvent = cart.items.reduce((n, item) => n + (ticket(item, config) && !ours.has(item) ? item.quantity : 0), 0);
    const otherVariant = cart.items.reduce((n, item) => n + (String(item.variant_id) === String(state.variantId) && !ours.has(item) ? item.quantity : 0), 0);
    return Math.max(0, Math.min(config.maxQuantity - otherEvent,
      variant.inventory === null ? Infinity : variant.inventory - otherVariant,
      variant.max === null ? Infinity : variant.max - otherVariant));
  }
  function checkBounds(cart, state, config) {
    const variant = config.variants.find(v => String(v.id) === String(state.variantId));
    if (!variant?.available || !config.purchasable) throw new FlowError('unavailable');
    if (variant.min !== 1 || variant.increment !== 1) throw new FlowError('unsupportedRules');
    if (state.attendees.length > allowance(cart, state, config)) throw new FlowError('inventory');
  }
  function same(item, target) {
    return item.quantity === 1 && String(item.variant_id) === String(target.id) && stable(item.properties || {}) === stable(target.properties);
  }
  function targets(state, labels) {
    return state.attendees.map(person => ({ id: String(state.variantId), quantity: 1, properties: properties(person, state, labels) }));
  }
  function exact(cart, state, labels) {
    const rows = members(cart, state);
    return legacy(cart, state).length === 0 && rows.length === state.attendees.length && targets(state, labels).every(target => {
      const matches = rows.filter(item => item.properties[P.id] === target.properties[P.id]);
      return matches.length === 1 && same(matches[0], target);
    });
  }
  function invalidGroup(cart, config) {
    const rows = cart.items.filter(item => ticket(item, config));
    const ids = new Set();
    for (const item of rows) {
      const p = item.properties || {};
      const identity = `${p[P.group]}:${p[P.id]}`;
      if (!clean(p[P.first]) || !clean(p[P.last]) || !p[P.id] || !p[P.group] || p[P.event] !== config.eventId || item.quantity !== 1 || ids.has(identity)) return item;
      if (![['First name', 'Last name'], ['Vorname', 'Nachname'], ['Имя', 'Фамилия']].some(([first, last]) => p[first] === p[P.first] && p[last] === p[P.last])) return item;
      ids.add(identity);
    }
    if (rows.reduce((n, item) => n + item.quantity, 0) > config.maxQuantity) return rows[0];
    return null;
  }
  function checkCart(cart, config) {
    const groups = new Set();
    for (const row of cart.items.filter(item => ticket(item, config))) {
      const groupId = row.properties?.[P.group];
      if (groups.has(groupId)) continue;
      groups.add(groupId);
      checkBounds(cart, { eventId: config.eventId, groupId, variantId: row.variant_id, attendees: fromCart(cart, groupId, config.eventId) }, config);
    }
  }
  class Client {
    constructor(rootUrl, fetcher = globalThis.fetch.bind(globalThis)) { this.root = rootUrl; this.fetcher = fetcher; }
    async request(path, body) {
      const response = await this.fetcher(this.root + path, {
        credentials: 'same-origin', cache: 'no-store',
        headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { method: 'POST', body: JSON.stringify(body) } : {}),
      });
      const value = await response.json();
      if (!response.ok || value.status || value.errors) throw new FlowError(response.status === 422 ? 'inventory' : 'network');
      return value;
    }
    async read() {
      const cart = await this.request('cart.js');
      if (!Array.isArray(cart.items)) throw new FlowError('network');
      return cart;
    }
    add(items) { return this.request('cart/add.js', { items }); }
    change(id, quantity, properties) { return this.request('cart/change.js', { id, quantity, ...(properties ? { properties } : {}) }); }
  }
  class Saver {
    constructor(client, persist, refresh) { this.client = client; this.persist = persist; this.refresh = refresh; this.busy = false; }
    async save(state, labels) {
      if (this.busy) throw new FlowError('busy');
      this.busy = true;
      try {
        validate(state);
        const config = await this.refresh();
        let cart = await this.client.read();
        checkBounds(cart, state, config);
        if (state.pending) {
          const before = state.pending.before;
          const operation = state.pending.operation;
          const changed = fingerprint(cart, state) !== before;
          if (changed && !this.operationCompatible(cart, state, operation, before)) throw new FlowError('conflict');
          state.base = fingerprint(cart, state);
          state.pending = null;
          this.persist(state);
        } else if (state.base !== undefined && state.base !== fingerprint(cart, state)) {
          throw new FlowError('conflict');
        }
        const eventSnapshot = snapshot(config);
        if (eventSnapshot) state.eventSnapshot = eventSnapshot;
        const desired = targets(state, labels);
        const wanted = new Map(desired.map(item => [item.properties[P.id], item]));
        let steps = 0;
        while (!exact(cart, state, labels)) {
          if (++steps > state.attendees.length * 4 + 20) throw new FlowError('verification');
          checkBounds(cart, state, config);
          let operation;
          const rows = members(cart, state);
          const seen = new Set();
          const remove = [...legacy(cart, state), ...rows.filter(item => {
            const id = item.properties[P.id];
            const duplicate = seen.has(id); seen.add(id);
            return !wanted.has(id) || duplicate || String(item.variant_id) !== String(state.variantId);
          })][0];
          if (remove) operation = { kind: 'remove', key: remove.key, previous: remove };
          else {
            const update = rows.find(item => !same(item, wanted.get(item.properties[P.id])));
            if (update) operation = { kind: 'change', key: update.key, previous: update, target: wanted.get(update.properties[P.id]) };
            else operation = { kind: 'add', targets: desired.filter(target => !rows.some(item => item.properties[P.id] === target.properties[P.id])) };
          }
          state.pending = { before: fingerprint(cart, state), operation };
          this.persist(state);
          let failure;
          try {
            if (operation.kind === 'add') await this.client.add(operation.targets);
            else await this.client.change(operation.key, operation.kind === 'remove' ? 0 : 1, operation.target?.properties);
          } catch (error) { failure = error; }
          cart = await this.client.read();
          if (!this.operationCompatible(cart, state, operation, state.pending.before)) throw new FlowError('conflict');
          if (!this.operationApplied(cart, state, operation)) {
            if (fingerprint(cart, state) !== state.pending.before && operation.kind !== 'add') throw new FlowError('conflict');
            state.base = fingerprint(cart, state);
            state.pending = null;
            this.persist(state);
            throw new FlowError(failure?.code || 'network');
          }
          state.base = fingerprint(cart, state);
          state.pending = null;
          this.persist(state);
        }
        const verifiedConfig = await this.refresh();
        cart = await this.client.read();
        checkBounds(cart, state, verifiedConfig);
        const verifiedSnapshot = snapshot(verifiedConfig);
        if (verifiedSnapshot && stable(verifiedSnapshot) !== stable(state.eventSnapshot || {})) throw new FlowError('verification');
        if (!exact(cart, state, labels)) throw new FlowError('verification');
        state.base = fingerprint(cart, state);
        state.saved = true;
        state.legacyKeys = [];
        this.persist(state);
        return { cart, config: verifiedConfig };
      } catch (error) {
        if (error instanceof FlowError) throw error;
        throw new FlowError('network');
      } finally { this.busy = false; }
    }
    operationCompatible(cart, state, operation, before) {
      // A lost response may represent any subset of a batch add. Accept only
      // that operation's exact effects; concurrent edits still require review.
      const signature = item => JSON.stringify([String(item.variant_id ?? item.id), item.quantity, stable(item.properties || {})]);
      const current = owned(cart, state).map(signature);
      const baseline = JSON.parse(before).map(row => JSON.stringify(row));
      const take = (list, value) => { const i = list.indexOf(value); if (i < 0) return false; list.splice(i, 1); return true; };
      if (operation.kind === 'add') {
        for (const row of baseline) if (!take(current, row)) return false;
        for (const target of operation.targets) take(current, signature(target));
        return current.length === 0;
      }
      if (!operation.previous) return this.operationApplied(cart, state, operation);
      take(baseline, signature(operation.previous));
      for (const row of baseline) if (!take(current, row)) return false;
      if (current.length === 0) return operation.kind === 'remove';
      return current.length === 1 && (current[0] === signature(operation.previous) || (operation.target && current[0] === signature(operation.target)));
    }
    operationApplied(cart, state, operation) {
      if (operation.kind === 'remove') return !cart.items.some(item => item.key === operation.key);
      const expected = operation.kind === 'add' ? operation.targets : [operation.target];
      const rows = members(cart, state);
      return expected.every(target => rows.some(item => item.properties[P.id] === target.properties[P.id] && same(item, target)));
    }
  }
  return { P, uuid, clean, snapshot, FlowError, ticket, members, owned, fromCart, fingerprint, properties, validate, allowance, checkBounds, exact, invalidGroup, checkCart, Client, Saver };
});
